import { makeStyles } from "@fluentui/react-components";
import {
  AirplaneLandingFilled,
  AirplaneTakeOffFilled,
} from "@fluentui/react-icons";
import React, { useContext } from "react";
import { LayoutContext } from "../../../../../../context/LayoutContext";
import {
  $cloud100,
  $cloud30,
  $cloud60,
  $cloud70,
} from "../../../../../../cssjs/colors";
import { IFlightTabsListCard } from "../../../../../../models/flights/IFlightListCard";
import { formatTime } from "../../../../../../utils/common/DateHelperFunctions";
import { FlightStatus } from "../FlightStatus";
import LiveTag from "./LiveTag";

const spaceBetween = "space-between";

export const FlightListCard: React.FC<IFlightTabsListCard> = (
  props
): React.JSX.Element => {
  const classes = useStyles();
  const { setSelectedFlight } = useContext(LayoutContext);

  const {
    id,
    flightNumber,
    aircraftTypeName,
    callSign,
    tailNumber,
    origin,
    destination,
    totalPax,
    trafficType,
    liveTracked,
    legType,
    sobt,
    eobt,
    aobt,
    sibt,
    eibt,
    aibt,
    progressPercentage,
  } = props;

  const flightTrackingProps = {
    origin,
    destination,
    progressPercentage,
  };

  const trafficText = trafficType === "International" ? "INTL" : "DOM";
  const actualTimeDeparture = formatTime(aobt || eobt || sobt);
  const actualTimeArrival = formatTime(sibt || eibt || aibt);

  const handleClick = () => {
    setSelectedFlight({
      flightDocID: id,
      flightID: flightNumber,
    });
  };

  return (
    <>
      <section
        className={classes.card}
        onClick={handleClick}
        data-testid="flight-list-card"
      >
        <div className={`${classes.cardHeader} amd-container-vcenter`}>
          <div className={classes.cardDetails}>
            <div
              className={`${classes.cardTitleSection} amd-container-vcenter`}
            >
              <div className="amd-container-vcenter">
                <div className={classes.cardTitleText}>{flightNumber}</div>
                <div className={"fs-20"}>
                  {legType === "arrival" ? (
                    <AirplaneLandingFilled />
                  ) : (
                    <AirplaneTakeOffFilled />
                  )}
                </div>
                {liveTracked && (
                  <div className={classes.liveTag}>
                    <LiveTag liveTracked={liveTracked} />
                  </div>
                )}
              </div>
              <div>
                <FlightStatus key={id} {...flightTrackingProps} />
              </div>
            </div>
            <div className={`${classes.cardSubTitle} amd-container-hspacebtwn`}>
              <div>{aircraftTypeName}</div>
              <div>{tailNumber}</div>
              <div>{callSign}</div>
            </div>
          </div>
        </div>
        <div className={`${classes.cardFooter} amd-container-hspacebtwn`}>
          <div>
            <span className={classes.cardFooterHighlight}>ATD </span>
            {actualTimeDeparture}
          </div>
          <div>
            <span className={classes.cardFooterHighlight}>ETA </span>
            {actualTimeArrival}
          </div>
          <div>
            <span className={classes.cardFooterHighlight}>PAX </span>
            {totalPax}
          </div>
          <div>
            <span className={classes.cardFooterHighlight}>{trafficText}</span>
          </div>
        </div>
      </section>
    </>
  );
};

const useStyles = makeStyles({
  card: {
    maxHeight: "80px",
    borderBottom: `1px solid ${$cloud30}`,
    display: "flex",
    width: "100%",
    flexDirection: "column",
    color: $cloud100,
    padding: "12px 0 8px",
    ":hover": {
      cursor: "pointer",
    },
  },
  cardHeader: {
    paddingRight: "4px",
  },
  cardDetails: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  cardTitleSection: {
    justifyContent: spaceBetween,
  },
  cardTitleText: {
    fontSize: "16px",
    paddingRight: "12px",
    marginBottom: "1px",
  },
  cardSubTitle: {
    color: $cloud70,
    fontSize: "12px",
  },
  cardFooter: {
    fontSize: "12px",
    paddingRight: "4px",
  },
  cardFooterHighlight: {
    color: $cloud60,
  },
  flightDetailsPosition: {
    position: "absolute",
    zIndex: 1,
    width: "100%",
    top: "0",
    left: "0",
    background: $cloud100,
  },
  liveTag: {
    "& .fui-Tag ": {
      maxHeight: "14px",
    },
    "& .fui-Tag__primaryText": {
      lineHeight: "1.2",
    },
  },
});
